import { useEffect, useRef } from 'react'

/** Silent introduction. Playback only runs while the video is actually visible. */
export function WelcomeVideo() {
	const ref = useRef<HTMLVideoElement>(null)
	useEffect(() => {
		const video = ref.current
		if (!video) return
		const motion = window.matchMedia('(prefers-reduced-motion: reduce)')
		const connection = (
			navigator as Navigator & { connection?: { saveData?: boolean } }
		).connection
		let wantsPlayback = !motion.matches && !connection?.saveData
		let inView = false
		let automaticPause = false
		const sync = () => {
			if (
				document.visibilityState === 'visible' &&
				inView &&
				wantsPlayback &&
				!video.ended
			) {
				void video.play().catch(() => {
					/* Native controls remain available if autoplay is blocked. */
				})
			} else if (!video.paused) {
				automaticPause = true
				video.pause()
			}
		}
		const onPause = () => {
			if (automaticPause) automaticPause = false
			else wantsPlayback = false
		}
		const onPlay = () => {
			wantsPlayback = true
		}
		const onMotion = () => {
			if (motion.matches) {
				wantsPlayback = false
				sync()
			}
		}
		const observer = new IntersectionObserver(
			([entry]) => {
				inView = entry.isIntersecting && entry.intersectionRatio >= 0.5
				sync()
			},
			{ threshold: [0, 0.5] },
		)
		observer.observe(video)
		video.addEventListener('pause', onPause)
		video.addEventListener('play', onPlay)
		document.addEventListener('visibilitychange', sync)
		motion.addEventListener('change', onMotion)
		return () => {
			observer.disconnect()
			video.removeEventListener('pause', onPause)
			video.removeEventListener('play', onPlay)
			document.removeEventListener('visibilitychange', sync)
			motion.removeEventListener('change', onMotion)
			video.pause()
		}
	}, [])
	return (
		<video
			ref={ref}
			className="welcome-video"
			src="/videos/erda-welcome.mp4"
			poster="/videos/erda-welcome-poster.jpg"
			width={960}
			height={540}
			muted
			playsInline
			controls
			preload="none"
			aria-label="Introducing Erda: claim a place, add a memory and share it"
		/>
	)
}
